import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, Plus, Wallet, User, Play, Trophy, Sparkles, Users, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence, PanInfo } from 'framer-motion'
import { pageContainer, fadeInUp, cardReveal, hoverLift, tapScale } from '@/lib/motion'
import { EmptyState } from '@/components/shared/EmptyState'

const CODE_LENGTH = 6

// Mock data for featured rooms
const featuredRooms = [
  { id: '1', stakes: '1/2', players: 5, maxPlayers: 9, commission: 5, status: 'in_hand', spectators: 12, host: null, isHot: true, avgPot: 4500, handsPerHour: 32 },
  { id: '5', stakes: '5/10', players: 6, maxPlayers: 9, commission: 5, status: 'in_hand', spectators: 15, host: null, isHot: false, avgPot: 22000, handsPerHour: 26 },
  { id: '3', stakes: '25/50', players: 8, maxPlayers: 9, commission: 5, status: 'in_hand', spectators: 28, host: null, isHot: true, avgPot: 95000, handsPerHour: 24 },
]

// Hero slides
const heroSlides = [
  {
    badge: '累積獎金',
    badgeIcon: Trophy,
    title: <>在高額桌<br /><span className="text-gradient-gold">贏取大獎</span></>,
    subtitle: '加入專屬 VIP 牌桌，完成每日挑戰',
    cta: '立即加入',
    accentGlow: 'bg-amber-400/20',
    deco: '♠',
  },
  {
    badge: '本週活動',
    badgeIcon: Sparkles,
    title: <><span className="text-gradient-gold">雙倍獎金</span><br />活動進行中</>,
    subtitle: '所有累積獎池獎金加倍，限時三天',
    cta: '查看詳情',
    accentGlow: 'bg-purple-400/15',
    deco: '♦',
  },
  {
    badge: '新手專區',
    badgeIcon: Users,
    title: <>免費參加<br /><span className="text-gradient-gold">新手錦標賽</span></>,
    subtitle: '零買入，前三名贏取真實獎金',
    cta: '免費報名',
    accentGlow: 'bg-emerald-400/15',
    deco: '♣',
  },
]

export function HomeScreen() {
  const navigate = useNavigate()
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const [showEmptyRooms, setShowEmptyRooms] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  // Auto-advance hero slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  const handleCodeChange = (index: number, value: string) => {
    const char = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(-1)
    const newCode = [...code]
    newCode[index] = char
    setCode(newCode)
    if (char && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, CODE_LENGTH)
    const newCode = [...code]
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i]
    }
    setCode(newCode)
    const focusIndex = Math.min(pastedData.length, CODE_LENGTH - 1)
    inputRefs.current[focusIndex]?.focus()
  }

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.x < -50) {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length)
    } else if (info.offset.x > 50) {
      setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)
    }
  }

  const isCodeComplete = code.every(c => c !== '')

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={pageContainer}
      className="flex flex-col gap-6 pb-24 pt-4 relative"
    >
      {/* Noise overlay only (ambient glow removed — now handled by global components) */}
      <div className="fixed inset-0 noise-overlay pointer-events-none" />

      {/* Hero Section with Carousel */}
      <motion.section variants={fadeInUp} className="px-4 relative z-10">
        <div className="relative h-64 rounded-2xl overflow-hidden bg-surface-sunken">
          {/* Ambient glow — shifts per slide */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`bg-${currentSlide}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0"
            >
              <div className={cn('absolute -top-20 -right-20 w-56 h-56 rounded-full blur-[80px]', heroSlides[currentSlide].accentGlow)} />
              <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-gold/8 rounded-full blur-[60px]" />
            </motion.div>
          </AnimatePresence>

          {/* Decorative suit — cropped top-right corner */}
          <AnimatePresence mode="wait">
            <motion.span
              key={`deco-${currentSlide}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.06 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute -top-[48px] -right-[3px] text-[280px] font-serif leading-none select-none pointer-events-none text-foreground"
            >
              {heroSlides[currentSlide].deco}
            </motion.span>
          </AnimatePresence>

          {/* Top border highlight */}
          <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-gold/20 to-transparent" />

          {/* Slides */}
          <motion.div
            className="absolute inset-0 cursor-grab active:cursor-grabbing"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                className="absolute inset-0 flex flex-col justify-center px-6 z-10"
              >
                {/* Badge */}
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold/10 border border-gold/25 w-fit mb-3">
                  {(() => { const Icon = heroSlides[currentSlide].badgeIcon; return <Icon className="w-3 h-3 text-gold" /> })()}
                  <span className="text-[10px] font-bold text-gold uppercase tracking-wider">
                    {heroSlides[currentSlide].badge}
                  </span>
                </div>
                {/* Title */}
                <h2 className="font-display text-[28px] font-semibold text-foreground mb-2 leading-[1.2] tracking-tight">
                  {heroSlides[currentSlide].title}
                </h2>
                {/* Subtitle + CTA row */}
                <p className="text-muted-foreground/70 text-[13px] max-w-[220px] leading-relaxed mb-3">
                  {heroSlides[currentSlide].subtitle}
                </p>
                <button className="w-fit px-4 py-1.5 rounded-lg bg-gold/15 border border-gold/30 text-gold text-xs font-bold tracking-wide hover:bg-gold/25 transition-colors">
                  {heroSlides[currentSlide].cta} →
                </button>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Bottom: progress segments */}
          <div className="absolute bottom-4 left-6 right-6 z-20 flex gap-2">
            {heroSlides.map((_, i) => (
              <div key={i} className="flex-1 h-[3px] rounded-full bg-foreground/[0.08] overflow-hidden">
                {i === currentSlide ? (
                  <motion.div
                    key={`seg-${currentSlide}`}
                    className="h-full bg-gold/70 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 5, ease: 'linear' }}
                  />
                ) : i < currentSlide ? (
                  <div className="h-full w-full bg-gold/30 rounded-full" />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Quick Actions */}
      <motion.section variants={fadeInUp} className="px-4 relative z-10">
        <div className="grid grid-cols-4 gap-3">
          {[
            { icon: Play, label: '開始遊戲', action: () => navigate('/rooms'), accent: true },
            { icon: Plus, label: '創建房間', action: () => navigate('/create-room'), accent: false },
            { icon: Wallet, label: '錢包儲值', action: () => navigate('/wallet'), accent: false },
            { icon: User, label: '個人資料', action: () => navigate('/profile'), accent: false },
          ].map((item, i) => (
            <motion.button
              key={i}
              variants={cardReveal}
              onClick={item.action}
              whileHover={hoverLift}
              whileTap={tapScale}
              className={cn(
                "group flex flex-col items-center gap-2 p-3 rounded-2xl transition-colors duration-200",
                item.accent
                  ? "bg-gradient-to-b from-gold to-gold-deep text-background shadow-glow-gold"
                  : "glass-card hover:border-gold/30"
              )}
            >
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center mb-1 transition-colors duration-200",
                item.accent ? "bg-white/20" : "bg-surface-elevated group-hover:bg-gold/10"
              )}>
                <item.icon className={cn(
                  "w-6 h-6 transition-colors duration-200",
                  item.accent ? "text-background" : "text-muted-foreground group-hover:text-gold"
                )} />
              </div>
              <span className={cn(
                "text-xs font-semibold transition-colors duration-200",
                item.accent ? "text-background" : "text-muted-foreground group-hover:text-foreground"
              )}>{item.label}</span>
            </motion.button>
          ))}
        </div>
      </motion.section>

      {/* Quick Join - Apple Style Passcode */}
      <motion.section variants={fadeInUp} className="px-4 relative z-10">
        <div className="glass-card rounded-2xl p-4">
          <p className="text-xs text-muted-foreground text-center mb-3 font-medium">輸入房間代碼</p>
          <div className="flex items-center justify-center gap-2 mb-4">
            {Array.from({ length: CODE_LENGTH }).map((_, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="text"
                maxLength={1}
                value={code[index]}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={cn(
                  "w-11 h-14 rounded-xl bg-surface-elevated border-2 text-center text-xl font-mono font-bold text-foreground",
                  "focus:outline-none focus:border-gold focus:bg-surface-elevated transition-all duration-200",
                  "placeholder:text-muted-foreground/20",
                  code[index] ? "border-foreground/20" : "border-foreground/10"
                )}
              />
            ))}
          </div>
          <motion.button
            whileTap={tapScale}
            disabled={!isCodeComplete}
            className={cn(
              "w-full h-12 rounded-xl font-bold text-sm tracking-wide transition-all duration-200",
              isCodeComplete
                ? "btn-primary"
                : "bg-surface-elevated text-muted-foreground/50 cursor-not-allowed border border-foreground/5"
            )}
          >
            加入房間
          </motion.button>
        </div>
      </motion.section>

      {/* Popular Rooms */}
      <motion.section variants={fadeInUp} className="px-4 relative z-10">
        <div className="flex items-center justify-between mb-4 px-1">
          <h3 className="font-display font-semibold text-xl text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gold" />
            熱門牌桌
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEmptyRooms(!showEmptyRooms)}
              className={cn(
                "text-[10px] font-mono px-2 py-1 rounded-lg transition-all border border-dashed",
                showEmptyRooms ? "bg-rose-500/10 text-rose-400 border-rose-500/30" : "bg-transparent text-muted-foreground/40 border-foreground/10"
              )}
            >
              [DEV] 空狀態
            </button>
            <button
              onClick={() => navigate('/rooms')}
              className="text-xs font-semibold text-gold/70 hover:text-gold transition-colors duration-200 flex items-center gap-1"
            >
              查看全部
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {showEmptyRooms ? (
          <EmptyState variant="no-rooms" action={{ label: '瀏覽房間', onClick: () => navigate('/rooms') }} />
        ) : (
        <div className="flex flex-col gap-3">
          {featuredRooms.map((room, index) => (
            <motion.div
              key={room.id}
              variants={cardReveal}
              initial="hidden"
              animate="visible"
              transition={{ delay: index * 0.05 }}
              onClick={() => navigate(`/room/${room.id}`)}
              whileHover={hoverLift}
              whileTap={tapScale}
              className={cn(
                "group relative p-4 rounded-2xl glass-card transition-colors duration-200 overflow-hidden cursor-pointer",
                room.isHot && "hot-glow border-gold/30"
              )}
            >
              {/* Status dot */}
              <div className={cn(
                "absolute top-4 right-4 flex items-center gap-1.5",
                room.isHot && "top-4 right-4"
              )}>
                {room.isHot && (
                  <span className="text-[10px] font-bold text-gold uppercase tracking-wider">🔥 熱門</span>
                )}
                {!room.isHot && (
                  <div className="flex items-center gap-1.5">
                    <span className={cn(
                      "relative flex h-2 w-2",
                    )}>
                      {room.status === 'in_hand' && (
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      )}
                      <span className={cn(
                        "relative inline-flex rounded-full h-2 w-2",
                        room.status === 'in_hand' ? "bg-emerald-500" : "bg-muted-foreground/40"
                      )} />
                    </span>
                    <span className={cn(
                      "text-[10px] font-semibold",
                      room.status === 'in_hand' ? "text-emerald-400" : "text-muted-foreground/60"
                    )}>
                      {room.status === 'in_hand' ? '進行中' : '等待中'}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3.5 relative z-10">
                {/* Stakes badge */}
                <div className="w-14 h-14 rounded-2xl bg-surface-elevated flex flex-col items-center justify-center border border-foreground/5 group-hover:border-gold/20 transition-colors duration-200 flex-shrink-0">
                  <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wide leading-none mb-0.5">盲注</span>
                  <span className="text-lg font-bold text-gradient-gold leading-none">${room.stakes}</span>
                </div>

                {/* Room info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-sm text-foreground font-semibold">德州撲克</span>
                    {room.host && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gold/10 text-gold border border-gold/20 font-bold flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" />
                        私人
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {room.players}/{room.maxPlayers}
                    </span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground/10" />
                    <span>{room.commission}%</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground/10" />
                    <span>均鍋 ${(room.avgPot / 100).toFixed(0)}</span>
                  </div>
                </div>

                <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-gold transition-colors duration-200 flex-shrink-0" />
              </div>
            </motion.div>
          ))}
        </div>
        )}

      </motion.section>
    </motion.div>
  )
}
